// Tickets persona · list (/tickets) — round-3 re-skin (PLAN-W27 D / FRONTEND-BLUEPRINT §3.5).
// PageHeader + DataTable over api.listTickets(): subject · status · composite priority · kind ·
// created · due, with a status filter + a library-backed ticket-kind filter. Ticket CREATE stays
// honestly gated: it requires a ticket_kind (FK → admin_ticket_kind), and that canonical library is
// counsel-gated content (AC-D18-7) that ships EMPTY — when it is empty the create button is absent
// and an honest note explains why (same pattern as auditor engagements / transfer mechanisms). The
// dialog below activates automatically once the library is seeded. Detail lives at /tickets/:id.
import { useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { TENANT_PATHS } from "@sympllex/api-client";
import { api } from "../lib/api";
import { Can } from "../lib/rbac";
import { useI18n } from "../lib/i18n";
import { PageHeader } from "@/components/page-header";
import { DataTable, type Column } from "@/components/data-table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { DeactivateButton, errText, type Row } from "./inspector/_shared";

// Mirrors @sympllex/domain TICKET_LIFECYCLES (ticket_lifecycle enum) — kept inline so the browser
// bundle does not pull in the domain package's DB (drizzle) runtime. The generic lifecycle is the
// ONLY workflow the domain supports today: per-kind state machines live in counsel-gated content.
export const TICKET_LIFECYCLES = ["draft", "open", "in_progress", "blocked", "in_review", "done", "cancelled", "deferred"] as const;
// biv_scale_5 — the 5-axis priority scale (B-METHODOLOGY).
export const PRIORITY_SCALE = ["very_low", "low", "medium", "high", "very_high"] as const;
export const PRIORITY_AXES = [
  "axisRegulatorySeverity", "axisOperationalImpact", "axisImplementationEffort", "axisDependencyDepth", "axisDeadlineProximity",
] as const;

type BadgeVariant = "default" | "secondary" | "destructive" | "outline";
const LIFECYCLE_VARIANTS: Record<string, BadgeVariant> = {
  draft: "secondary", open: "default", in_progress: "default", blocked: "destructive",
  in_review: "outline", done: "secondary", cancelled: "secondary", deferred: "outline",
};

/** Lifecycle badge with the nl/en label from the catalog (falls back to the raw token). */
export function LifecycleBadge({ value }: { value: unknown }) {
  const { t } = useI18n();
  if (value == null || value === "") return <span className="text-muted-foreground">—</span>;
  const token = String(value);
  const key = `tk.lc.${token}`;
  const label = t(key);
  return <Badge variant={LIFECYCLE_VARIANTS[token] ?? "outline"}>{label === key ? token.replace(/_/g, " ") : label}</Badge>;
}

/** biv_scale_5 value label (falls back to the raw token). */
export function scaleLabel(t: (k: string) => string, value: unknown): string {
  if (value == null || value === "") return "—";
  const key = `tk.scale.${String(value)}`;
  const label = t(key);
  return label === key ? String(value).replace(/_/g, " ") : label;
}

export const fmtDate = (v: unknown): string => (v ? new Date(String(v)).toLocaleDateString() : "—");
export const selectCls = "h-8 rounded-2xl border border-transparent bg-input/50 px-2.5 text-sm outline-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/30";

export interface TicketKind { id: string; code: string; displayName: string; category: string | null }

export function TicketsRoute() {
  const { t } = useI18n();
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ["tickets"] });
  const ticketsQ = useQuery({ queryKey: ["tickets"], queryFn: () => api.listTickets() });
  // Canonical ticket-kind library — counsel-gated content; EMPTY until seeded (degrade honestly).
  const kindsQ = useQuery({ queryKey: ["ticket-kinds"], queryFn: () => api.listTicketKinds() });
  const kinds = (kindsQ.data ?? []) as unknown as TicketKind[];
  const kindName = useMemo(() => new Map(kinds.map((k) => [k.id, k.displayName])), [kinds]);

  const [q, setQ] = useState("");
  const [lcFilter, setLcFilter] = useState("");
  const [kindFilter, setKindFilter] = useState("");

  const allRows = (ticketsQ.data ?? []) as Row[];
  const needle = q.trim().toLowerCase();
  const rows = useMemo(
    () => allRows.filter((r) =>
      (!needle || String(r.title ?? "").toLowerCase().includes(needle))
      && (!lcFilter || String(r.lifecycle) === lcFilter)
      && (!kindFilter || String(r.ticketKindId) === kindFilter)),
    [allRows, needle, lcFilter, kindFilter],
  );

  const columns: Column<Row>[] = [
    {
      key: "title", header: t("tk.col.title"),
      render: (r) => (
        <Link to="/tickets/$id" params={{ id: String(r.id) }} className="font-medium text-primary hover:underline">
          {String(r.title ?? "—")}
        </Link>
      ),
    },
    { key: "lifecycle", header: t("tk.col.status"), render: (r) => <LifecycleBadge value={r.lifecycle} /> },
    {
      key: "priority", header: t("tk.col.priority"), align: "right",
      render: (r) => <span className="font-medium tabular-nums">{r.priority == null ? "—" : String(r.priority)}</span>,
    },
    { key: "ticketKindId", header: t("tk.col.kind"), render: (r) => <KindCell id={r.ticketKindId} name={kindName.get(String(r.ticketKindId))} /> },
    { key: "createdAt", header: t("tk.col.created"), render: (r) => <span className="text-muted-foreground">{fmtDate(r.createdAt)}</span> },
    { key: "dueAt", header: t("tk.col.due"), render: (r) => <span className="text-muted-foreground">{fmtDate(r.dueAt)}</span> },
  ];

  return (
    <section>
      <Can perm="ticket.read" fallback={<p className="text-sm text-muted-foreground">{t("crud.readonly")}</p>}>
        <PageHeader
          title={t("nav.tickets")}
          description={t("tk.sub")}
          actions={kinds.length > 0 ? (
            <Can perm="ticket.write"><CreateTicketDialog kinds={kinds} onDone={invalidate} /></Can>
          ) : undefined}
        />
        {/* Honest create-gate: the ticket-kind library is counsel-gated and ships empty. */}
        {!kindsQ.isLoading && kinds.length === 0 && (
          <p className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">{t("tk.createBlocked")}</p>
        )}
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <div className="max-w-sm flex-1 basis-56">
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("crud.search")} aria-label={t("crud.search")} />
          </div>
          <select value={lcFilter} onChange={(e) => setLcFilter(e.target.value)} aria-label={t("tk.filter.status")} className={selectCls}>
            <option value="">{t("tk.filter.allStatuses")}</option>
            {TICKET_LIFECYCLES.map((lc) => <option key={lc} value={lc}>{t(`tk.lc.${lc}`)}</option>)}
          </select>
          <select value={kindFilter} onChange={(e) => setKindFilter(e.target.value)} aria-label={t("tk.filter.kind")} className={selectCls} disabled={kinds.length === 0}>
            <option value="">{t("tk.filter.allKinds")}</option>
            {kinds.map((k) => <option key={k.id} value={k.id}>{k.displayName}</option>)}
          </select>
          {!kindsQ.isLoading && kinds.length === 0 && <span className="text-xs text-muted-foreground">{t("tk.kindsEmpty")}</span>}
        </div>
        {ticketsQ.error ? (
          <p className="text-sm text-destructive">{t("crud.failed")} {errText(ticketsQ.error)}</p>
        ) : (
          <DataTable<Row>
            columns={columns}
            rows={rows}
            loading={ticketsQ.isLoading}
            empty={needle || lcFilter || kindFilter ? t("tk.noMatches") : t("tk.empty")}
            getRowId={(r) => String(r.id ?? "")}
            rowActions={(r) => (
              <Can perm="ticket.delete">
                <DeactivateButton basePath={TENANT_PATHS.tickets} id={String(r.id)} onDone={invalidate} />
              </Can>
            )}
          />
        )}
      </Can>
    </section>
  );
}

/** Kind cell: displayName when the library row resolves; otherwise the bare FK, shortened and
 *  honest (the canonical library is empty until counsel-gated content lands). */
function KindCell({ id, name }: { id: unknown; name?: string | undefined }) {
  if (name) return <Badge variant="secondary">{name}</Badge>;
  if (id == null || id === "") return <span className="text-muted-foreground">—</span>;
  const s = String(id);
  return <span className="font-mono text-xs text-muted-foreground" title={s}>{s.slice(0, 8)}…</span>;
}

/** Create dialog — only rendered once the counsel-gated ticket-kind library is seeded (kinds ≥ 1).
 *  Kind + title required; body + the 5 priority axes optional (the DB trigger computes the
 *  composite score from the axes on insert). */
function CreateTicketDialog({ kinds, onDone }: { kinds: TicketKind[]; onDone: () => void }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [f, setF] = useState<Record<string, string>>({});
  const set = (k: string, v: string) => setF((s) => ({ ...s, [k]: v }));
  const reset = () => setF({});
  const mut = useMutation({
    mutationFn: () => api.createTicket({
      ticketKindId: f.ticketKindId!,
      title: f.title!.trim(),
      ...(f.body?.trim() ? { body: f.body.trim() } : {}),
      ...Object.fromEntries(PRIORITY_AXES.map((a) => [a, f[a]]).filter(([, v]) => v)),
    } as Parameters<typeof api.createTicket>[0]),
    onSuccess: () => { reset(); setOpen(false); onDone(); },
  });
  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { reset(); mut.reset(); } }}>
      <DialogTrigger asChild>
        <Button size="sm">{t("crud.new")}</Button>
      </DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("tk.new")}</DialogTitle>
          <DialogDescription>{t("tk.sub")}</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          <div className="grid gap-1.5">
            <Label htmlFor="tk-kind">{t("tk.field.kind")} *</Label>
            <select id="tk-kind" value={f.ticketKindId ?? ""} onChange={(e) => set("ticketKindId", e.target.value)} className={`${selectCls} w-full`}>
              <option value="">{t("crud.select")}</option>
              {kinds.map((k) => <option key={k.id} value={k.id}>{k.displayName}</option>)}
            </select>
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="tk-title">{t("tk.col.title")} *</Label>
            <Input id="tk-title" value={f.title ?? ""} onChange={(e) => set("title", e.target.value)} />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="tk-body">{t("tk.field.body")}</Label>
            <Textarea id="tk-body" rows={3} value={f.body ?? ""} onChange={(e) => set("body", e.target.value)} />
          </div>
          {PRIORITY_AXES.map((a) => (
            <div key={a} className="grid gap-1.5">
              <Label htmlFor={`tk-${a}`}>{t(`tk.axis.${a}`)}</Label>
              <select id={`tk-${a}`} value={f[a] ?? ""} onChange={(e) => set(a, e.target.value)} className={`${selectCls} w-full`}>
                <option value="">{t("crud.select")}</option>
                {PRIORITY_SCALE.map((s) => <option key={s} value={s}>{scaleLabel(t, s)}</option>)}
              </select>
            </div>
          ))}
          {mut.isError && <p className="text-sm text-destructive">{errText(mut.error)}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => { setOpen(false); reset(); mut.reset(); }}>{t("common.cancel")}</Button>
          <Button onClick={() => mut.mutate()} disabled={mut.isPending || !f.ticketKindId || !f.title?.trim()}>
            {mut.isPending ? t("crud.creating") : t("common.create")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
